/**
 * LocationHeader — Single, condensed location bar shared across every
 * location-aware surface (Home hero, Weather, Prayer, AroundMe, Watch list…).
 *
 * Composes the three existing primitives into ONE clickable pill:
 *   - Auto-detect (GPS via LocationPrimingCard → navigator.geolocation)
 *   - Favorites (WatchedCitiesService — up to 12 saved cities, 1-tap switch)
 *   - Search (CityPicker — bundled list + Photon API)
 *
 * UX:
 *   ┌────────────────────────────────────────────┐
 *   │ 📍 Casablanca · MA              ⭐ ↻       │   ← collapsed (default)
 *   └────────────────────────────────────────────┘
 *
 *   When tapped, expands inline to:
 *
 *   ┌────────────────────────────────────────────┐
 *   │ 📍 Casablanca · MA              ⭐ ↻       │
 *   │ ─────────────────────────────────────────  │
 *   │ Favorites: [Paris] [Mecca] [Rabat]  [+]    │   ← horizontal scroll
 *   │ [📡 Use my GPS]   [🔍 Search a city]       │
 *   └────────────────────────────────────────────┘
 *
 * The component is a SINGLE source of truth for choosing a location.
 * Every consuming widget/module reads `getLocationService().getState()` and
 * subscribes to `.onChange()` — no widget re-implements the picker.
 *
 * Per CLAUDE.md "Home Page Anti-Regression": this file does NOT fetch /
 * import / await anything blocking on mount. GPS prompt only triggers when
 * the user explicitly taps "Use my GPS" inside the priming sheet.
 */

import type { AweniContext } from '../types';
import { getLocationService } from '../services/LocationService';
import { getWatchedCitiesService } from '../services/WatchedCitiesService';
import { prayerTimeService } from '../services/PrayerTimeService';
import { LocationPrimingCard } from '../modules/home/LocationPrimingCard';
import { CityPicker } from '../modules/home/CityPicker';
import type { WatchedCity } from '../modules/home/types';

export interface LocationHeaderDeps {
  ctx: AweniContext;
  /** Optional callback — fired AFTER the user changes city (GPS / favorite /
   *  search). Use it to refetch widget data; the LocationService listener
   *  fires automatically too, this is just sugar for one-off tasks. */
  onChange?: () => void;
  /** Compact mode — favorites + actions hidden by default, shown on tap.
   *  `false` keeps everything always visible (used in dedicated module
   *  pages like Weather/Prayer where vertical space is plentiful). */
  collapsible?: boolean;
}

export class LocationHeader {
  private root: HTMLElement | null = null;
  private deps: LocationHeaderDeps;
  private locUnsub: (() => void) | null = null;
  private favUnsub: (() => void) | null = null;
  private expanded = false;

  constructor(deps: LocationHeaderDeps) {
    this.deps = { collapsible: true, ...deps };
  }

  mount(parent: HTMLElement): void {
    if (this.root) return;
    this.injectStyles();

    const root = document.createElement('section');
    root.className = 'aweni-locheader';
    root.setAttribute('role', 'region');
    root.setAttribute('aria-label', this.deps.ctx.i18n.t('location_header_aria') || 'Location');
    parent.appendChild(root);
    this.root = root;
    this.render();

    // React to ANY location change (GPS, favorite tap, manual pick).
    this.locUnsub = getLocationService().onChange(() => this.render());
    this.favUnsub = getWatchedCitiesService().onChange(() => this.render());
  }

  unmount(): void {
    this.locUnsub?.();
    this.favUnsub?.();
    this.locUnsub = null;
    this.favUnsub = null;
    this.root?.remove();
    this.root = null;
  }

  // ----------------------------- rendering -------------------------------

  private render(): void {
    if (!this.root) return;
    const t = this.deps.ctx.i18n.t.bind(this.deps.ctx.i18n);
    const loc = getLocationService().getState();
    const cities = getWatchedCitiesService().list();
    const collapsible = this.deps.collapsible !== false;
    const expanded = !collapsible || this.expanded;

    const cityName = loc.city || t('home_hero_no_city') || 'Set your city';
    const country = loc.countryCode ? ` · ${loc.countryCode}` : '';
    const isCurrentSaved = loc.lat !== null && loc.lng !== null && cities.some((c) => sameSpot(c, loc.lat!, loc.lng!));

    const pinSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>`;
    const refreshSvg = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>`;
    const starFilled = `<svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;
    const starEmpty = `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round" stroke-linecap="round" aria-hidden="true"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>`;

    // The expanded panel is appended ONLY when expanded — keeps the DOM
    // light when collapsed (collapsible=true is default).
    this.root.innerHTML = `
      <button type="button" class="aweni-loc-pill" data-action="toggle"
              aria-expanded="${expanded}" aria-controls="aweni-loc-panel">
        <span class="aweni-loc-pin" aria-hidden="true">${pinSvg}</span>
        <span class="aweni-loc-city">${escHtml(cityName)}${escHtml(country)}</span>
        <span class="aweni-loc-actions">
          ${loc.lat !== null && loc.lng !== null
            ? `<span class="aweni-loc-icon-btn" data-action="toggle-fav"
                     role="button" tabindex="0"
                     aria-label="${escAttr(isCurrentSaved
                       ? (t('location_header_unsave') || 'Remove from favorites')
                       : (t('location_header_save') || 'Save to favorites'))}"
                     style="${isCurrentSaved ? 'color:#D4AF37;' : ''}">
                 ${isCurrentSaved ? starFilled : starEmpty}
               </span>`
            : ''}
          <span class="aweni-loc-icon-btn" data-action="refresh"
                role="button" tabindex="0"
                aria-label="${escAttr(t('location_header_refresh') || 'Refresh location')}">
            ${refreshSvg}
          </span>
        </span>
      </button>

      ${expanded ? `
        <div id="aweni-loc-panel" class="aweni-loc-panel" role="region">
          ${(() => {
            // Hide the currently-active city from the favorites row — it
            // already shows in the pill above. Showing it twice (once in
            // the pill, once as a highlighted chip) was redundant and
            // visually confusing in the v1 deploy.
            const others = cities.filter((c) => !sameSpot(c, loc.lat ?? NaN, loc.lng ?? NaN));
            return others.length > 0 ? `
            <div class="aweni-loc-favrow" role="list"
                 aria-label="${escAttr(t('location_header_favorites') || 'Favorite cities')}">
              ${others.map((c) => `
                <button type="button" class="aweni-loc-favchip" role="listitem"
                        data-action="pick-fav" data-id="${escAttr(c.id)}">
                  📍 ${escHtml(c.name)}
                </button>`).join('')}
            </div>
            ` : '';
          })()}
          <div class="aweni-loc-ctas">
            <button type="button" class="aweni-loc-cta" data-action="gps">
              📡 ${escHtml(t('location_header_gps') || 'Use my GPS')}
            </button>
            <button type="button" class="aweni-loc-cta" data-action="search">
              🔍 ${escHtml(t('location_header_search') || 'Search a city')}
            </button>
          </div>
        </div>
      ` : ''}
    `;

    this.wireHandlers();
  }

  private wireHandlers(): void {
    if (!this.root) return;
    const root = this.root;

    // Pill toggles the panel (collapsible mode only). Clicks on the action
    // icons inside must NOT toggle — handled by stopPropagation below.
    root.querySelector('[data-action="toggle"]')?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      // If user tapped the star/refresh icons (or their svg), let those
      // handlers run — don't toggle the panel.
      if (target.closest('[data-action="toggle-fav"]') || target.closest('[data-action="refresh"]')) {
        return;
      }
      if (this.deps.collapsible !== false) {
        this.expanded = !this.expanded;
        this.render();
      }
    });

    root.querySelector('[data-action="refresh"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleGps();
    });
    root.querySelector('[data-action="toggle-fav"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleFavorite();
    });

    root.querySelector('[data-action="gps"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleGps();
    });
    root.querySelector('[data-action="search"]')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.handleSearch();
    });

    root.querySelectorAll<HTMLElement>('[data-action="pick-fav"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const id = btn.dataset.id || '';
        this.pickFavorite(id);
      });
    });
  }

  // ----------------------------- actions ---------------------------------

  private handleGps(): void {
    // Apple-compliant priming: NEVER cold-prompt. The card explains why we
    // need location and triggers navigator.geolocation only on user tap.
    const card = new LocationPrimingCard({
      ctx: this.deps.ctx,
      locationService: getLocationService(),
      onResolved: () => this.afterChange(),
    });
    card.show();
  }

  private handleSearch(): void {
    const picker = new CityPicker({
      ctx: this.deps.ctx,
      locationService: getLocationService(),
      onResolved: () => this.afterChange(),
    });
    picker.show();
  }

  private pickFavorite(id: string): void {
    const c = getWatchedCitiesService().list().find((x) => x.id === id);
    if (!c) return;
    getLocationService().setManualLocation(c.lat, c.lng, c.name, c.countryCode);
    this.afterChange();
  }

  private toggleFavorite(): void {
    const loc = getLocationService().getState();
    if (loc.lat === null || loc.lng === null || !loc.city) return;
    const svc = getWatchedCitiesService();
    const existing = svc.list().find((c) => sameSpot(c, loc.lat!, loc.lng!));
    if (existing) {
      svc.remove(existing.id);
    } else {
      svc.add({
        name: loc.city,
        countryCode: loc.countryCode || undefined,
        lat: loc.lat,
        lng: loc.lng,
      });
    }
    this.render();
  }

  private afterChange(): void {
    // Sync prayer-time service so all consumers (PrayerCountdown, Memoriz,
    // PrayerModule) see the new coords without a separate notify.
    const loc = getLocationService().getState();
    if (loc.lat !== null && loc.lng !== null) {
      try { prayerTimeService.setLocation(loc.lat, loc.lng, loc.city || undefined); } catch { /* */ }
    }
    this.expanded = false;
    this.deps.onChange?.();
    this.render();
  }

  // ----------------------------- styles ----------------------------------

  private injectStyles(): void {
    if (document.getElementById('aweni-locheader-styles')) return;
    const style = document.createElement('style');
    style.id = 'aweni-locheader-styles';
    style.textContent = `
      .aweni-locheader { display: flex; flex-direction: column; gap: 8px; }
      .aweni-loc-pill {
        display: flex; align-items: center; gap: 8px;
        width: 100%;
        padding: 8px 12px;
        min-height: 40px;
        background: var(--card-bg, #fff);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 999px;
        color: var(--text-primary, #1E293B);
        font: inherit; font-size: 13px; font-weight: 600;
        cursor: pointer; text-align: start;
        transition: border-color 120ms ease, box-shadow 120ms ease;
      }
      .aweni-loc-pill:hover { border-color: var(--accent, #0A0A0A); }
      .aweni-loc-pill:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A); outline-offset: 2px;
      }
      .dark .aweni-loc-pill {
        background: rgba(255,255,255,0.04);
        border-color: rgba(255,255,255,0.10);
        color: #F1F5F9;
      }
      .aweni-loc-pin {
        flex-shrink: 0; display: inline-flex; align-items: center;
        color: var(--accent, #0A0A0A);
      }
      .aweni-loc-city {
        flex: 1; min-width: 0;
        white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
      }
      .aweni-loc-actions {
        display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0;
      }
      .aweni-loc-icon-btn {
        display: inline-flex; align-items: center; justify-content: center;
        width: 28px; height: 28px;
        border-radius: 8px;
        cursor: pointer;
        color: var(--text-secondary, #6B7280);
        transition: background 120ms ease, color 120ms ease;
      }
      .aweni-loc-icon-btn:hover {
        background: rgba(10,10,10,0.06);
        color: var(--accent, #0A0A0A);
      }
      .aweni-loc-icon-btn:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A); outline-offset: 1px;
      }
      .dark .aweni-loc-icon-btn:hover { background: rgba(255,255,255,0.08); }

      .aweni-loc-panel {
        display: flex; flex-direction: column; gap: 8px;
        animation: aweniLocFadeIn 160ms ease;
      }
      @keyframes aweniLocFadeIn {
        from { opacity: 0; transform: translateY(-4px); }
        to   { opacity: 1; transform: translateY(0); }
      }
      .aweni-loc-favrow {
        display: flex; gap: 6px;
        overflow-x: auto;
        padding-bottom: 4px;
        scrollbar-width: thin;
        -webkit-overflow-scrolling: touch;
      }
      .aweni-loc-favrow::-webkit-scrollbar { height: 4px; }
      .aweni-loc-favchip {
        flex-shrink: 0;
        padding: 6px 12px;
        min-height: 32px;
        background: var(--bg-secondary, #F3F4F6);
        border: 1px solid transparent;
        border-radius: 999px;
        font: inherit; font-size: 12px; font-weight: 600;
        color: var(--text-primary, #1E293B);
        cursor: pointer; white-space: nowrap;
        transition: background 120ms ease, border-color 120ms ease;
      }
      .aweni-loc-favchip:hover { border-color: var(--accent, #0A0A0A); }
      .aweni-loc-favchip[aria-current="true"] {
        background: var(--accent, #0A0A0A);
        color: var(--accent-text, #fff);
      }
      .aweni-loc-favchip:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A); outline-offset: 1px;
      }
      .dark .aweni-loc-favchip {
        background: rgba(255,255,255,0.05);
        color: #F1F5F9;
      }
      .aweni-loc-ctas {
        display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
      }
      .aweni-loc-cta {
        padding: 10px 12px;
        min-height: 40px;
        background: var(--bg-secondary, #F3F4F6);
        border: 1px solid var(--border, #E5E7EB);
        border-radius: 12px;
        font: inherit; font-size: 13px; font-weight: 600;
        color: var(--text-primary, #1E293B);
        cursor: pointer;
        text-align: center;
        transition: border-color 120ms ease, transform 120ms ease;
      }
      .aweni-loc-cta:hover { border-color: var(--accent, #0A0A0A); }
      .aweni-loc-cta:active { transform: scale(0.98); }
      .aweni-loc-cta:focus-visible {
        outline: 2px solid var(--accent, #0A0A0A); outline-offset: 2px;
      }
      .dark .aweni-loc-cta {
        background: rgba(255,255,255,0.05);
        border-color: rgba(255,255,255,0.10);
        color: #F1F5F9;
      }
    `;
    document.head.appendChild(style);
  }
}

function sameSpot(c: WatchedCity, lat: number, lng: number): boolean {
  if (!isFinite(lat) || !isFinite(lng)) return false;
  return c.lat.toFixed(2) === lat.toFixed(2) && c.lng.toFixed(2) === lng.toFixed(2);
}

function escHtml(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escAttr(s: string | undefined | null): string {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
